import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Modal,
  Typography,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  Button,
  Fade,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Chip,
  Autocomplete,
  FormControlLabel,
  Switch,
  Tooltip,
  Checkbox,
  StepConnector,
  FormHelperText,
  Collapse,
  Divider,
} from "@mui/material";
import {
  Close as CloseIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  CheckCircle as CheckCircleIcon,
  HomeWork as HomeWorkIcon,
  Weekend as WeekendIcon,
  Payments as PaymentsIcon,
  Description as DescriptionIcon,
  AddPhotoAlternate as AddPhotoAlternateIcon,
  Stairs as StairsIcon,
  Bed as BedIcon,
  Bathtub as BathtubIcon,
  Countertops as CountertopsIcon,
  FamilyRestroom as FamilyRestroomIcon,
  Person as PersonIcon,
  Chair as ChairIcon,
  LocalParking as LocalParkingIcon,
  Pool as PoolIcon,
  Elevator,
  Wifi,
  SportsGymnastics,
  RoofingRounded,
  BalconyOutlined,
  People,
  MedicalServices,
  FireExtinguisher,
} from "@mui/icons-material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { useFormik } from "formik";
import { styled, useTheme as useMuiTheme, alpha } from "@mui/material/styles";
import LocationPickerMap from "../../../../components/LocationPickerMap";
import {
  flatValidationSchema,
  landlordConditionOptions,
  leaseDurationOptions,
  noticePeriodOptions,
  MONTH_OPTIONS,
} from "../validationSchema";
import { MdSmokeFree } from "react-icons/md";
import {
  LATE_FEE_TYPES,
  emptyLatePolicyForm,
  latePolicyFromForm,
  DEFAULT_GRACE_DAYS,
} from "../../../../utils/latePolicy";

// The admin's current platform-wide late-fee policy, in the same shape the
// (now read-only) form field expects. The owner's property form only ever
// displays this — see LatePolicyField below and addFlat.js on the server,
// which stamps every save with whatever this resolves to at that moment.
const adminLatePolicyToForm = (adminPolicy) => ({
  enabled: true,
  type: adminPolicy?.type || LATE_FEE_TYPES.PER_DAY_PERCENTAGE,
  amount: adminPolicy?.amount != null ? String(adminPolicy.amount) : "",
  gracePeriodDays: String(adminPolicy?.gracePeriodDays ?? DEFAULT_GRACE_DAYS),
  maximumAmount: "",
  maximumPercentage: adminPolicy?.maximumPercentage != null ? String(adminPolicy.maximumPercentage) : "",
});

const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  alternativeLabel: {
    top: 22,
  },
  "& .MuiStepConnector-line": {
    height: 3,
    border: 0,
    backgroundColor: "#eaeaf0",
    borderRadius: 1,
  },
  "&.Mui-active .MuiStepConnector-line": {
    backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  },
  "&.Mui-completed .MuiStepConnector-line": {
    backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  },
}));

const ColorlibStepIconRoot = styled("div")(({ theme, ownerState }) => ({
  backgroundColor:
    ownerState.completed || ownerState.active ? "transparent" : "#ccc",
  zIndex: 1,
  color: "#fff",
  width: 50,
  height: 50,
  display: "flex",
  borderRadius: "50%",
  justifyContent: "center",
  alignItems: "center",
  backgroundImage:
    ownerState.active || ownerState.completed
      ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
      : "none",
  boxShadow: ownerState.active
    ? `0 8px 18px -10px ${theme.palette.primary.main}`
    : "none",
  transition: "all 0.3s ease",
}));

const stepIcons = {
  1: <HomeWorkIcon />,
  2: <WeekendIcon />,
  3: <PaymentsIcon />,
  4: <DescriptionIcon />,
  5: <AddPhotoAlternateIcon />,
};

function ColorlibStepIcon(props) {
  const { active, completed, className, icon } = props;
  return (
    <ColorlibStepIconRoot
      ownerState={{ completed, active }}
      className={className}
    >
      {completed ? <CheckCircleIcon /> : stepIcons[String(icon)]}
    </ColorlibStepIconRoot>
  );
}

const formSteps = [
  { label: "Basic Info", description: "Property details" },
  { label: "Location & Photos", description: "Map & images" },
  { label: "Features", description: "Amenities & facilities" },
  { label: "Pricing", description: "Rent & charges" },
  { label: "Terms", description: "Agreement conditions" },
];

// Will be used in future for city selection based on country
const countryCityMap = {
  Pakistan: [
    { value: "Karachi", label: "Karachi" },
    // { value: "Lahore", label: "Lahore" },
    // { value: "Islamabad", label: "Islamabad" },
  ],
  // India: [
  //   { value: "Delhi", label: "Delhi" },
  //   { value: "Mumbai", label: "Mumbai" },
  //   { value: "Bangalore", label: "Bangalore" },
  // ],
  // UAE: [
  //   { value: "Dubai", label: "Dubai" },
  //   { value: "Abu Dhabi", label: "Abu Dhabi" },
  // ],
};

// The platform's late-payment policy — the admin's decision now, not the
// owner's (see SystemSettings.latePaymentPolicy / CommissionSettings.jsx).
// Every field here is disabled: this is purely what the property will be
// saved with, never something the owner edits. The server enforces this too
// — it overwrites whatever this field submits with the admin's current
// policy regardless (addFlat.js), so this being read-only is a UI courtesy,
// not the only guard.
const LatePolicyField = ({ formik }) => {
  const value = formik.values.latePolicy || emptyLatePolicyForm();
  const isPercentageDay = value.type === LATE_FEE_TYPES.PER_DAY_PERCENTAGE;
  const isFixedAfterGrace = value.type === LATE_FEE_TYPES.FIXED_AFTER_GRACE;

  const adornment = (text, position = "start") => ({
    [position === "start" ? "startAdornment" : "endAdornment"]: (
      <Typography sx={{ [position === "start" ? "mr" : "ml"]: 1, color: "#999" }}>
        {text}
      </Typography>
    ),
  });

  return (
    <Grid item xs={12} data-field="latePolicy">
      <Typography
        variant="subtitle2"
        fontWeight={600}
        sx={{ mt: 1, mb: 0.5, color: "text.secondary" }}
      >
        Late Payment Policy
      </Typography>
      <Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />} sx={{ mb: 2 }}>
        Set by OTS for the whole platform — applies to every property and can't be changed here.
      </Alert>
      {/* A visibly distinct, read-only card — everything inside it is the
          platform's rule, not a choice this form makes. Ordered to read as a
          sentence: grace period first (how long after the due date above),
          then what the fee actually is, then its ceiling. */}
      <Box
        sx={{
          bgcolor: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: 2,
          p: 2,
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              disabled
              label="Grace Period"
              value={value.gracePeriodDays}
              InputProps={adornment("Days", "end")}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth disabled>
              <InputLabel id="late-fee-type-label">Fee Type</InputLabel>
              <Select
                labelId="late-fee-type-label"
                label="Fee Type"
                value={value.type || LATE_FEE_TYPES.PER_DAY_PERCENTAGE}
              >
                <MenuItem value={LATE_FEE_TYPES.PER_DAY_PERCENTAGE}>% of rent, per day</MenuItem>
                <MenuItem value={LATE_FEE_TYPES.PER_MONTH_PERCENTAGE}>% of rent, per month</MenuItem>
                <MenuItem value={LATE_FEE_TYPES.FIXED_AFTER_GRACE}>Fixed charge (one-time)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              disabled
              label={isFixedAfterGrace ? "Fee Amount" : "Fee Rate"}
              value={value.amount}
              InputProps={
                isFixedAfterGrace
                  ? adornment("Rs")
                  : adornment(isPercentageDay ? "% / day" : "% / month", "end")
              }
            />
          </Grid>
          {!isFixedAfterGrace && (
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                disabled
                label="Maximum Late Fee"
                value={value.maximumPercentage}
                InputProps={adornment("% of Rent", "end")}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <FormHelperText sx={{ mt: -1 }}>
              Late fee applies after the grace period — counted from the rent due date above.
            </FormHelperText>
          </Grid>
        </Grid>
      </Box>
    </Grid>
  );
};

// Amount + billing frequency for Maintenance/Water/Security/Trash. When the
// owner picks "Annual", a Due Month select reveals so the smart bill-upload
// flow knows which single cycle to surface that charge in.
const ChargeFrequencyField = ({ name, label, formik }) => {
  const value = formik.values[name] || {};
  const touched = formik.touched[name] || {};
  const errors = formik.errors[name] || {};
  const isAnnual = value.frequency === "annual";

  const setField = (field, v) =>
    formik.setFieldValue(name, { ...value, [field]: v });
  const setTouchedField = (field) =>
    formik.setFieldTouched(`${name}.${field}`, true, false);

  return (
    <Grid item xs={12} data-field={name}>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label={label}
            name={`${name}.amount`}
            type="number"
            value={value.amount}
            onChange={(e) => setField("amount", e.target.value)}
            onBlur={() => setTouchedField("amount")}
            error={touched.amount && Boolean(errors.amount)}
            helperText={touched.amount && errors.amount}
            InputProps={{
              startAdornment: (
                <Typography sx={{ mr: 1, color: "#999" }}>Rs</Typography>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={isAnnual ? 4 : 8}>
          <FormControl fullWidth>
            <InputLabel>Frequency</InputLabel>
            <Select
              label="Frequency"
              name={`${name}.frequency`}
              value={value.frequency || "monthly"}
              onChange={(e) => setField("frequency", e.target.value)}
              onBlur={() => setTouchedField("frequency")}
            >
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="annual">Annual (once a year)</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4} sx={{ display: isAnnual ? "block" : "none" }}>
          <Collapse in={isAnnual}>
            <FormControl
              fullWidth
              error={touched.dueMonth && Boolean(errors.dueMonth)}
            >
              <InputLabel>Due Month</InputLabel>
              <Select
                label="Due Month"
                name={`${name}.dueMonth`}
                value={value.dueMonth || ""}
                onChange={(e) => setField("dueMonth", e.target.value)}
                onBlur={() => setTouchedField("dueMonth")}
              >
                {MONTH_OPTIONS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
              {touched.dueMonth && errors.dueMonth && (
                <FormHelperText>{errors.dueMonth}</FormHelperText>
              )}
            </FormControl>
          </Collapse>
        </Grid>
      </Grid>
    </Grid>
  );
};

const propertySuggestions = [
  "Newly Renovated",
  "24/7 Water",
  "Family Friendly",
  "Peaceful",
  "Corner Flat",
  "No Load-Shedding",
  "Load-Shedding",
  "No Brokers",
];

const MUTUALLY_EXCLUSIVE_TAGS = ["Load-Shedding", "No Load-Shedding"];

const PropertyFormModal = ({
  open,
  handleClose,
  editingFlat,
  validateUser,
  dispatch,
  FlatRegisters,
  fetchFlats,
  toast,
  hasFormChanged,
  originalValues,
  setOriginalValues,
  activeStep,
  setActiveStep,
  /* Called with the new flat's id after a successful create, so the page can
     walk the owner to the next step instead of a toast doing it. */
  onCreated,
}) => {
  const formRef = useRef(null);
  const modalBodyRef = useRef(null);

  /* Selected states used to be painted indigo/teal regardless of the theme */
  const muiTheme = useMuiTheme();
  const accent = muiTheme.palette.primary.main;
  const accentSoft = alpha(accent, 0.09);

  // The admin's current late-fee policy — read-only display for
  // LatePolicyField. MyProperty.jsx (the parent) dispatches GetSystemSettings
  // on mount; this just reads whatever the store already has.
  const { settings: systemSettings } = useSelector((state) => state.systemSettings);

  const formik = useFormik({
    initialValues: {
      flatName: "",
      flatNumber: "",
      flatFloor: "",
      flatRooms: "",
      flatToilet: "",
      flatKitchen: "",
      flatRent: "",
      flatAdvance: "",
      maintenance: { amount: "", frequency: "monthly", dueMonth: "" },
      water: { amount: "", frequency: "monthly", dueMonth: "" },
      security: { amount: "", frequency: "monthly", dueMonth: "" },
      trash: { amount: "", frequency: "monthly", dueMonth: "" },
      personAllowed: "",
      petAllowed: false,
      parkingAvailable: false,
      furnished: false,
      smokeAlarm: false,
      firstAidKit: false,
      fireExtinguisher: false,
      pool: false,
      bathTub: false,
      elevator: false,
      wifi: false,
      balcony: false,
      gym: false,
      roof: false,
      country: "Pakistan",
      city: "Karachi",
      images: [],
      previews: [],
      latePolicy: emptyLatePolicyForm(),
      propertyAddress: "",
      lat: "",
      lng: "",
      mapAddress: "",
      note: "",
      is_rent: 0,
      is_active: true,
      tags: [],
      allowedFor: "family",
      landlordConditions: [],
      customConditions: "",
      rentDueDate: 5,
      noticePeriod: "1_month",
      rentIncrease: 10,
      leaseDuration: "11_months",
    },
    validationSchema: flatValidationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const userId = validateUser?.UserValidate?.data?.user?._id;
        const formData = new FormData();
        formData.append("userId", userId);
        const toBool = (v) => v === true || v === "true";

        Object.entries({
          flatName: values.flatName,
          flatNumber: values.flatNumber,
          flatFloor: values.flatFloor,
          flatRooms: values.flatRooms,
          flatToilet: values.flatToilet,
          flatKitchen: values.flatKitchen,
          flatRent: values.flatRent,
          flatAdvance: values.flatAdvance,
          flatMaintenanceAmount: values.maintenance.amount,
          flatMaintenanceFrequency: values.maintenance.frequency,
          flatMaintenanceDueMonth:
            values.maintenance.frequency === "annual"
              ? values.maintenance.dueMonth
              : "",
          flatWaterAmount: values.water.amount,
          flatWaterFrequency: values.water.frequency,
          flatWaterDueMonth:
            values.water.frequency === "annual" ? values.water.dueMonth : "",
          flatSecurityAmount: values.security.amount,
          flatSecurityFrequency: values.security.frequency,
          flatSecurityDueMonth:
            values.security.frequency === "annual"
              ? values.security.dueMonth
              : "",
          flatTrashAmount: values.trash.amount,
          flatTrashFrequency: values.trash.frequency,
          flatTrashDueMonth:
            values.trash.frequency === "annual" ? values.trash.dueMonth : "",
          personAllowed: values.personAllowed,
          petAllowed: toBool(values.petAllowed),
          parkingAvailable: toBool(values.parkingAvailable),
          furnished: toBool(values.furnished),
          smokeAlarm: toBool(values.smokeAlarm),
          firstAidKit: toBool(values.firstAidKit),
          fireExtinguisher: toBool(values.fireExtinguisher),
          pool: toBool(values.pool),
          bathTub: toBool(values.bathTub),
          elevator: toBool(values.elevator),
          wifi: toBool(values.wifi),
          balcony: toBool(values.balcony),
          gym: toBool(values.gym),
          roof: toBool(values.roof),
          country: values.country,
          city: values.city,
          is_approved: false,
          is_rejected: false,
          is_rent: Number(values.is_rent),
          // Only the policy is stored; the server validates it again and keeps
          // the two older late-fee fields in step with it.
          latePaymentPolicy: JSON.stringify(latePolicyFromForm(values.latePolicy)),
          propertyAddress: values.propertyAddress,
          lat: values.lat,
          lng: values.lng,
          mapAddress: values.mapAddress,
          note: values.note,
          is_active: values.is_active,
          tags: values.tags,
          allowedFor: values.allowedFor,
          landlordConditions: JSON.stringify(values.landlordConditions || []),
          customConditions: values.customConditions || "",
          rentDueDate: values.rentDueDate || 5,
          noticePeriod: values.noticePeriod || "1_month",
          rentIncrease: values.rentIncrease || 10,
          leaseDuration: values.leaseDuration || "11_months",
          // Owner always uploads bills — no landlord/tenant choice to make.
          bill_uploaded_by: "landlord",

          ...(values._id && { _id: values._id }),
        }).forEach(([key, value]) => {
          formData.append(`flatData[${key}]`, value);
        });

        if (values.images && values.images.length > 0) {
          values.images.forEach((file) => {
            formData.append("images", file);
          });
        }

        const res = await dispatch(FlatRegisters(formData));

        if (res?.payload?.data?.success) {
          await fetchFlats();

          if (editingFlat) {
            // Editing resets the approval and sends the property back to the
            // admin — the server's message states exactly this, and the
            // email says the same thing.
            toast.success(
              res?.payload?.data?.message ||
                "Your property has been updated successfully!"
            );
          } else {
            /* A brand new property is only half-listed: it cannot go to the
               admin until a setting is applied. That used to be a permanent
               toast with a button inside it that reloaded the whole page.
               The page now hands it to a proper next-step dialog, which the
               owner can act on or dismiss without losing anything. */
            const created =
              res?.payload?.data?.data || res?.payload?.data?.flat;
            onCreated?.(created?._id || null);
          }
          resetForm();
          setOriginalValues(null);
          handleClose();
          return;
        }

        toast.error(res?.payload?.data?.message || "Failed to add flat");
      } catch (err) {
        toast.error("Error saving flat");
      }
    },
  });

  useEffect(() => {
    const toBool = (v) => v === true || v === "true" || v === 1 || v === "1";
    // Old properties only have the flat *Charges fields (no frequency/dueMonth
    // concept); newer ones may have the split flat*Amount/*Frequency/*DueMonth
    // fields from this feature. Read both, always default frequency to
    // "monthly" so nothing pre-existing silently disappears from bills.
    const normalizeCharge = (legacyAmount, amount, frequency, dueMonth) => ({
      amount: amount ?? legacyAmount ?? "",
      frequency: frequency || "monthly",
      dueMonth: dueMonth || "",
    });
    if (editingFlat) {
      const initialValues = {
        ...editingFlat,
        maintenance: normalizeCharge(
          editingFlat.flatMaintananceCharges,
          editingFlat.flatMaintenanceAmount,
          editingFlat.flatMaintenanceFrequency,
          editingFlat.flatMaintenanceDueMonth
        ),
        water: normalizeCharge(
          undefined,
          editingFlat.flatWaterAmount,
          editingFlat.flatWaterFrequency,
          editingFlat.flatWaterDueMonth
        ),
        security: normalizeCharge(
          editingFlat.flatSecurityCharges,
          editingFlat.flatSecurityAmount,
          editingFlat.flatSecurityFrequency,
          editingFlat.flatSecurityDueMonth
        ),
        trash: normalizeCharge(
          editingFlat.flatTrashCharges,
          editingFlat.flatTrashAmount,
          editingFlat.flatTrashFrequency,
          editingFlat.flatTrashDueMonth
        ),
        // Always the admin's CURRENT policy, not whatever this property was
        // last saved with — that's what it will become the moment this form
        // is submitted (addFlat.js re-stamps it regardless), so showing
        // anything else here would just be wrong.
        latePolicy: adminLatePolicyToForm(systemSettings?.latePaymentPolicy),
        petAllowed: toBool(editingFlat.petAllowed),
        parkingAvailable: toBool(editingFlat.parkingAvailable),
        furnished: toBool(editingFlat.furnished),
        smokeAlarm: toBool(editingFlat.smokeAlarm),
        firstAidKit: toBool(editingFlat.firstAidKit),
        fireExtinguisher: toBool(editingFlat.fireExtinguisher),
        pool: toBool(editingFlat.pool),
        bathTub: toBool(editingFlat.bathTub),
        elevator: toBool(editingFlat.elevator),
        wifi: toBool(editingFlat.wifi),
        balcony: toBool(editingFlat.balcony),
        gym: toBool(editingFlat.gym),
        roof: toBool(editingFlat.roof),
        is_approved: toBool(editingFlat.is_approved),
        is_active: toBool(editingFlat.is_active),
        is_rent: Number(editingFlat.is_rent),
        images: editingFlat.images || [],
        previews: editingFlat.images || [],
        tags: editingFlat?.tags?.length > 0 ? editingFlat.tags.split(",") : [],
        allowedFor: editingFlat.allowedFor || "",
        landlordConditions: editingFlat.landlordConditions
          ? typeof editingFlat.landlordConditions === "string"
            ? JSON.parse(editingFlat.landlordConditions)
            : editingFlat.landlordConditions
          : [],
        customConditions: editingFlat.customConditions || "",
        rentDueDate: editingFlat.rentDueDate || 5,
        noticePeriod: editingFlat.noticePeriod || "1_month",
        rentIncrease: editingFlat.rentIncrease || 10,
        leaseDuration: editingFlat.leaseDuration || "11_months",
      };
      formik.setValues(initialValues);
      setOriginalValues(JSON.parse(JSON.stringify(initialValues)));
    } else {
      formik.resetForm();
      formik.setFieldValue("latePolicy", adminLatePolicyToForm(systemSettings?.latePaymentPolicy));
      setOriginalValues(null);
    }
  }, [
    editingFlat,
    open,
    systemSettings?.latePaymentPolicy,
    formik.setValues,
    formik.resetForm,
    formik.setFieldValue,
    setOriginalValues,
  ]);

  const handleNext = () => {
    setActiveStep((prevStep) => Math.min(prevStep + 1, formSteps.length - 1));
  };

  const handleBack = () => {
    setActiveStep((prevStep) => Math.max(prevStep - 1, 0));
  };

  const handleStepNext = async () => {
    const stepFields = {
      0: [
        "flatName",
        "flatNumber",
        "flatFloor",
        "flatRooms",
        "flatToilet",
        "flatKitchen",
      ],
      1: ["previews", "propertyAddress"],
      2: ["allowedFor", "tags"],
      3: [
        "flatRent",
        "flatAdvance",
        "maintenance",
        "water",
        "security",
        "trash",
        "personAllowed",
        "rentDueDate",
        "latePolicy",
      ],
      4: [],
    };

    const fieldsToValidate = stepFields[activeStep] || [];

    // Set fields as touched to show errors
    fieldsToValidate.forEach((field) => {
      // latePolicy is an object: mark each of its inputs touched so their
      // messages show, not just the group.
      formik.setFieldTouched(
        field,
        field === "latePolicy"
          ? {
              type: true,
              amount: true,
              gracePeriodDays: true,
              maximumAmount: true,
              maximumPercentage: true,
            }
          : true,
        false // Don't validate yet
      );
    });

    const errors = await formik.validateForm();

    let firstErrorField = null;
    let isValid = true;
    for (const field of fieldsToValidate) {
      if (errors[field]) {
        isValid = false;
        if (!firstErrorField) {
          firstErrorField = field;
        }
      }
    }

    if (isValid) {
      handleNext();
    } else if (firstErrorField) {
      // Scroll to the first error
      let errorElement =
        document.querySelector(`[name="${firstErrorField}"]`) ||
        document.querySelector(`[data-field="${firstErrorField}"]`);

      if (errorElement && modalBodyRef.current) {
        const container = modalBodyRef.current;
        const containerRect = container.getBoundingClientRect();
        const elementRect = errorElement.getBoundingClientRect();
        const offset =
          elementRect.top - containerRect.top + container.scrollTop - 80;
        container.scrollTo({
          top: offset,
          behavior: "smooth",
        });
        const input =
          errorElement.tagName === "INPUT"
            ? errorElement
            : errorElement.querySelector("input");
        input?.focus({ preventScroll: true });
      }
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const newPreviews = files.map((file) => URL.createObjectURL(file));
    formik.setFieldValue("images", [...formik.values.images, ...files]);
    formik.setFieldValue("previews", [
      ...formik.values.previews,
      ...newPreviews,
    ]);
  };

  const handleRemoveImage = (index) => {
    const updatedPreviews = formik.values.previews.filter(
      (_, i) => i !== index
    );
    const updatedImages = formik.values.images.filter((_, i) => i !== index);
    formik.setFieldValue("previews", updatedPreviews);
    formik.setFieldValue("images", updatedImages);
  };

  useEffect(() => {
    if (formik.submitCount > 0 && Object.keys(formik.errors).length > 0) {
      const firstErrorField = Object.keys(formik.errors)[0];
      let errorElement =
        document.querySelector(`[name="${firstErrorField}"]`) ||
        document.querySelector(`[data-field="${firstErrorField}"]`);
      if (errorElement && modalBodyRef.current) {
        const container = modalBodyRef.current;
        const containerRect = container.getBoundingClientRect();
        const elementRect = errorElement.getBoundingClientRect();
        const offset =
          elementRect.top - containerRect.top + container.scrollTop - 80;
        container.scrollTo({
          top: offset,
          behavior: "smooth",
        });
        setTimeout(() => {
          const input =
            errorElement.tagName === "INPUT"
              ? errorElement
              : errorElement.querySelector("input");
          input?.focus({ preventScroll: true });
        }, 300);
      }
    }
  }, [formik.errors, formik.submitCount]);

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          borderRadius: 3,
          width: { xs: "95%", sm: 600, md: 800 },
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            background: `linear-gradient(135deg, ${accent} 0%, ${muiTheme.palette.secondary.main} 100%)`,
            color: "white",
            p: 3,
            position: "relative",
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "white",
              "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h5" fontWeight={700}>
            {editingFlat ? "Edit Property" : "Add New Property"}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
            {formSteps[activeStep].description}
          </Typography>
        </Box>

        <LinearProgress
          variant="determinate"
          value={(activeStep / (formSteps.length - 1)) * 100}
          sx={{
            height: 4,
            "& .MuiLinearProgress-bar": {
              background: `linear-gradient(135deg, ${accent} 0%, ${muiTheme.palette.secondary.main} 100%)`,
            },
          }}
        />

        {/* Saving always resets is_approved, so a live property drops off the
            market until the admin re-approves it. Worth knowing BEFORE the
            owner changes a rent figure they didn't need to. */}
        {editingFlat?.is_approved && (
          <Box
            sx={{
              mx: 2,
              mt: 2,
              px: 2,
              py: 1.5,
              borderRadius: 2,
              bgcolor: "#fffbeb",
              border: "1px solid #fcd34d",
              display: "flex",
              gap: 1.25,
              alignItems: "flex-start",
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 20, color: "#b45309", mt: "1px" }} />
            <Typography variant="body2" sx={{ color: "#92400e" }}>
              This property is live. Saving any change sends it back to the
              admin for approval, and tenants can't see it until it is approved
              again — usually within 24 hours.
            </Typography>
          </Box>
        )}

        <Box sx={{ px: 2, pt: 3, pb: 1 }}>
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            connector={<ColorlibConnector />}
          >
            {formSteps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel StepIconComponent={ColorlibStepIcon}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: activeStep === index ? 700 : 400,
                      color:
                        activeStep === index ? accent : "text.secondary",
                      display: { xs: "none", sm: "block" },
                    }}
                  >
                    {step.label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Box ref={modalBodyRef} sx={{ p: 3, flex: 1, overflowY: "auto" }}>
          <form ref={formRef} onSubmit={formik.handleSubmit}>
            <Fade in={activeStep === 0} timeout={300}>
              <Box sx={{ display: activeStep === 0 ? "block" : "none" }}>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <HomeWorkIcon color="primary" /> Property Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Property Name"
                      name="flatName"
                      placeholder="e.g., Sunset Apartments"
                      value={formik.values.flatName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.flatName &&
                        Boolean(formik.errors.flatName)
                      }
                      helperText={
                        formik.touched.flatName && formik.errors.flatName
                      }
                      InputProps={{
                        startAdornment: (
                          <HomeWorkIcon sx={{ mr: 1, color: "#999" }} />
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Flat/Unit Number"
                      name="flatNumber"
                      placeholder="e.g., A-102"
                      value={formik.values.flatNumber}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.flatNumber &&
                        Boolean(formik.errors.flatNumber)
                      }
                      helperText={
                        formik.touched.flatNumber && formik.errors.flatNumber
                      }
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      label="Floor"
                      name="flatFloor"
                      type="number"
                      value={formik.values.flatFloor}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.flatFloor &&
                        Boolean(formik.errors.flatFloor)
                      }
                      helperText={
                        formik.touched.flatFloor && formik.errors.flatFloor
                      }
                      InputProps={{
                        startAdornment: (
                          <StairsIcon sx={{ mr: 1, color: "#999" }} />
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      label="Bedrooms"
                      name="flatRooms"
                      type="number"
                      value={formik.values.flatRooms}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.flatRooms &&
                        Boolean(formik.errors.flatRooms)
                      }
                      helperText={
                        formik.touched.flatRooms && formik.errors.flatRooms
                      }
                      InputProps={{
                        startAdornment: (
                          <BedIcon sx={{ mr: 1, color: "#999" }} />
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      label="Bathrooms"
                      name="flatToilet"
                      type="number"
                      value={formik.values.flatToilet}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.flatToilet &&
                        Boolean(formik.errors.flatToilet)
                      }
                      helperText={
                        formik.touched.flatToilet && formik.errors.flatToilet
                      }
                      InputProps={{
                        startAdornment: (
                          <BathtubIcon sx={{ mr: 1, color: "#999" }} />
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      label="Kitchens"
                      name="flatKitchen"
                      type="number"
                      value={formik.values.flatKitchen}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.flatKitchen &&
                        Boolean(formik.errors.flatKitchen)
                      }
                      helperText={
                        formik.touched.flatKitchen && formik.errors.flatKitchen
                      }
                      InputProps={{
                        startAdornment: (
                          <CountertopsIcon sx={{ mr: 1, color: "#999" }} />
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Country</InputLabel>
                      <Select
                        name="country"
                        disabled
                        value={formik.values.country}
                        onChange={(e) => {
                          formik.handleChange(e);
                          const selectedCountry = e.target.value;
                          const defaultCity =
                            countryCityMap[selectedCountry][0]?.value || "";
                          formik.setFieldValue("city", defaultCity);
                        }}
                        label="Country"
                      >
                        {Object.keys(countryCityMap).map((country) => (
                          <MenuItem key={country} value={country}>
                            {country}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>City</InputLabel>
                      <Select
                        name="city"
                        disabled
                        value={formik.values.city}
                        onChange={formik.handleChange}
                        label="City"
                      >
                        {countryCityMap[formik.values.country]?.map((c) => (
                          <MenuItem key={c.value} value={c.value}>
                            {c.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            </Fade>

            <Fade in={activeStep === 1} timeout={300}>
              <Box sx={{ display: activeStep === 1 ? "block" : "none" }}>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <AddPhotoAlternateIcon color="primary" /> Location & Photos
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <LocationPickerMap formik={formik} />
                </Box>

                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Upload property photos:
                </Typography>

                {formik.values.previews?.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1.5,
                      mb: 2,
                    }}
                  >
                    {formik.values.previews.map((src, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          position: "relative",
                          width: 100,
                          height: 100,
                          borderRadius: 2,
                          overflow: "hidden",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        }}
                      >
                        <img
                          src={src}
                          alt={`preview-${idx}`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveImage(idx)}
                          sx={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            background: "rgba(255,255,255,0.9)",
                            "&:hover": {
                              background: "#ff5252",
                              color: "white",
                            },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}

                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  startIcon={<AddPhotoAlternateIcon />}
                  sx={{
                    py: 3,
                    borderStyle: "dashed",
                    borderWidth: 2,
                    "&:hover": { borderStyle: "dashed", borderWidth: 2 },
                  }}
                >
                  Click to Upload Images
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </Button>
                {formik.touched.previews && formik.errors.previews && (
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ mt: 1, display: "block" }}
                    data-field="previews"
                  >
                    {formik.errors.previews}
                  </Typography>
                )}
              </Box>
            </Fade>

            <Fade in={activeStep === 2} timeout={300}>
              <Box sx={{ display: activeStep === 2 ? "block" : "none" }}>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <WeekendIcon color="primary" /> Features & Amenities
                </Typography>

                <Paper
                  elevation={0}
                  sx={{ p: 2, mb: 3, bgcolor: "#f8f9fa", borderRadius: 2 }}
                >
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    This property is suitable for:
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Paper
                        elevation={
                          formik.values.allowedFor === "family" ? 3 : 0
                        }
                        onClick={() =>
                          formik.setFieldValue("allowedFor", "family")
                        }
                        sx={{
                          p: 2,
                          cursor: "pointer",
                          textAlign: "center",
                          border:
                            formik.values.allowedFor === "family"
                              ? `2px solid ${accent}`
                              : "1px solid #e0e0e0",
                          borderRadius: 2,
                          bgcolor:
                            formik.values.allowedFor === "family"
                              ? accentSoft
                              : "white",
                          transition: "all 0.2s ease",
                          "&:hover": { borderColor: accent },
                        }}
                      >
                        <FamilyRestroomIcon
                          sx={{
                            fontSize: 40,
                            color:
                              formik.values.allowedFor === "family"
                                ? accent
                                : "#999",
                          }}
                        />
                        <Typography variant="subtitle1" fontWeight={600}>
                          Family
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={4}>
                      <Paper
                        elevation={
                          formik.values.allowedFor === "bachelor" ? 3 : 0
                        }
                        onClick={() =>
                          formik.setFieldValue("allowedFor", "bachelor")
                        }
                        sx={{
                          p: 2,
                          cursor: "pointer",
                          textAlign: "center",
                          border:
                            formik.values.allowedFor === "bachelor"
                              ? `2px solid ${accent}`
                              : "1px solid #e0e0e0",
                          borderRadius: 2,
                          bgcolor:
                            formik.values.allowedFor === "bachelor"
                              ? accentSoft
                              : "white",
                          transition: "all 0.2s ease",
                          "&:hover": { borderColor: accent },
                        }}
                      >
                        <PersonIcon
                          sx={{
                            fontSize: 40,
                            color:
                              formik.values.allowedFor === "bachelor"
                                ? accent
                                : "#999",
                          }}
                        />
                        <Typography variant="subtitle1" fontWeight={600}>
                          Bachelor
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={4}>
                      <Paper
                        elevation={formik.values.allowedFor === "both" ? 3 : 0}
                        onClick={() =>
                          formik.setFieldValue("allowedFor", "both")
                        }
                        sx={{
                          p: 2,
                          cursor: "pointer",
                          textAlign: "center",
                          border:
                            formik.values.allowedFor === "both"
                              ? `2px solid ${accent}`
                              : "1px solid #e0e0e0",
                          borderRadius: 2,
                          bgcolor:
                            formik.values.allowedFor === "both"
                              ? accentSoft
                              : "white",
                          transition: "all 0.2s ease",
                          "&:hover": { borderColor: accent },
                        }}
                      >
                        <People
                          sx={{
                            fontSize: 40,
                            color:
                              formik.values.allowedFor === "both"
                                ? accent
                                : "#999",
                          }}
                        />
                        <Typography variant="subtitle1" fontWeight={600}>
                          Both
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Paper>

                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Select available Amenities & Facilities:
                </Typography>
                <Grid container spacing={1.5}>
                  {[
                    {
                      key: "furnished",
                      label: "Furnished",
                      icon: <ChairIcon />,
                    },
                    {
                      key: "parkingAvailable",
                      label: "Parking",
                      icon: <LocalParkingIcon />,
                    },
                    { key: "pool", label: "Swimming Pool", icon: <PoolIcon /> },
                    {
                      key: "bathTub",
                      label: "Bath Tub",
                      icon: <BathtubIcon />,
                    },
                    { key: "elevator", label: "Elevator", icon: <Elevator /> },
                    { key: "wifi", label: "WIFI", icon: <Wifi /> },
                    {
                      key: "balcony",
                      label: "Balcony",
                      icon: <BalconyOutlined />,
                    },
                    { key: "gym", label: "GYM", icon: <SportsGymnastics /> },
                    {
                      key: "roof",
                      label: "Rooftop/Terrace",
                      icon: <RoofingRounded />,
                    },
                  ].map((amenity) => (
                    <Grid item xs={6} sm={4} key={amenity.key}>
                      <Paper
                        elevation={0}
                        onClick={() =>
                          formik.setFieldValue(
                            amenity.key,
                            !formik.values[amenity.key]
                          )
                        }
                        sx={{
                          p: 1.5,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          border: formik.values[amenity.key]
                            ? `2px solid ${accent}`
                            : "1px solid #e0e0e0",
                          borderRadius: 2,
                          bgcolor: formik.values[amenity.key]
                            ? "#e8f5e9"
                            : "white",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            borderColor: accent,
                            bgcolor: "#f5f5f5",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            color: formik.values[amenity.key]
                              ? accent
                              : "#999",
                          }}
                        >
                          {amenity.icon}
                        </Box>
                        <Typography
                          variant="body2"
                          fontWeight={formik.values[amenity.key] ? 600 : 400}
                        >
                          {amenity.label}
                        </Typography>
                        {formik.values[amenity.key] && (
                          <CheckCircleIcon
                            sx={{
                              ml: "auto",
                              color: accent,
                              fontSize: 20,
                            }}
                          />
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>

                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                  sx={{ mt: 2 }}
                >
                  Do you have any of these safety items?
                </Typography>
                <Grid container spacing={1.5}>
                  {[
                    {
                      key: "smokeAlarm",
                      label: "Smoke Alarm",
                      icon: <MdSmokeFree />,
                    },
                    {
                      key: "firstAidKit",
                      label: "First Aid Kit",
                      icon: <MedicalServices />,
                    },
                    {
                      key: "fireExtinguisher",
                      label: "Fire extinguisher",
                      icon: <FireExtinguisher />,
                    },
                  ].map((safety) => (
                    <Grid item xs={6} sm={4} key={safety.key}>
                      <Paper
                        elevation={0}
                        onClick={() =>
                          formik.setFieldValue(
                            safety.key,
                            !formik.values[safety.key]
                          )
                        }
                        sx={{
                          p: 1.5,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          border: formik.values[safety.key]
                            ? `2px solid ${accent}`
                            : "1px solid #e0e0e0",
                          borderRadius: 2,
                          bgcolor: formik.values[safety.key]
                            ? "#e8f5e9"
                            : "white",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            borderColor: accent,
                            bgcolor: "#f5f5f5",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            color: formik.values[safety.key]
                              ? accent
                              : "#999",
                          }}
                        >
                          {safety.icon}
                        </Box>
                        <Typography
                          variant="body2"
                          fontWeight={formik.values[safety.key] ? 600 : 400}
                        >
                          {safety.label}
                        </Typography>
                        {formik.values[safety.key] && (
                          <CheckCircleIcon
                            sx={{
                              ml: "auto",
                              color: accent,
                              fontSize: 20,
                            }}
                          />
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>

                <Box sx={{ mt: 3 }}>
                  <Autocomplete
                    multiple
                    freeSolo
                    disableCloseOnSelect
                    options={propertySuggestions.filter(
                      (tag) => !formik.values.tags.includes(tag)
                    )}
                    value={formik.values.tags}
                    onChange={(e, newValue) => {
                      const prevTags = formik.values.tags;
                      const justAdded = newValue.find(
                        (v) => !prevTags.includes(v)
                      );
                      let finalValue = newValue;
                      if (justAdded && MUTUALLY_EXCLUSIVE_TAGS.includes(justAdded)) {
                        const other = MUTUALLY_EXCLUSIVE_TAGS.find(
                          (v) => v !== justAdded
                        );
                        finalValue = newValue.filter((v) => v !== other);
                      }
                      formik.setFieldValue("tags", finalValue);
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="filled"
                          label={option}
                          {...getTagProps({ index })}
                          key={index}
                          sx={{ bgcolor: accent, color: "white" }}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Describe your property"
                        placeholder="Add tags like 'Near Metro', 'Newly Renovated'"
                        // error={
                        //   formik.touched.tags && Boolean(formik.errors.tags)
                        // }
                        // helperText={formik.touched.tags && formik.errors.tags}
                      />
                    )}
                  />
                </Box>
              </Box>
            </Fade>

            <Fade in={activeStep === 3} timeout={300}>
              <Box sx={{ display: activeStep === 3 ? "block" : "none" }}>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <PaymentsIcon color="primary" /> Pricing & Charges
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Monthly Rent (Rs)"
                      name="flatRent"
                      type="number"
                      value={formik.values.flatRent}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.flatRent &&
                        Boolean(formik.errors.flatRent)
                      }
                      helperText={
                        formik.touched.flatRent && formik.errors.flatRent
                      }
                      InputProps={{
                        startAdornment: (
                          <Typography sx={{ mr: 1, color: "#999" }}>
                            Rs
                          </Typography>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Advance Amount (Rs)"
                      name="flatAdvance"
                      type="number"
                      value={formik.values.flatAdvance}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.flatAdvance &&
                        Boolean(formik.errors.flatAdvance)
                      }
                      helperText={
                        formik.touched.flatAdvance && formik.errors.flatAdvance
                      }
                      InputProps={{
                        startAdornment: (
                          <Typography sx={{ mr: 1, color: "#999" }}>
                            Rs
                          </Typography>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Max Persons Allowed"
                      name="personAllowed"
                      type="number"
                      value={formik.values.personAllowed}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.personAllowed &&
                        Boolean(formik.errors.personAllowed)
                      }
                      helperText={
                        formik.touched.personAllowed &&
                        formik.errors.personAllowed
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      sx={{ mt: 1, mb: -1, color: "text.secondary" }}
                    >
                      Recurring Charges — how often is each collected?
                    </Typography>
                  </Grid>
                  <ChargeFrequencyField
                    name="maintenance"
                    label="Maintenance Charges (Rs)"
                    formik={formik}
                  />
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      <strong>Fixed Charges</strong> — Water, Security and Trash
                      are billed to the tenant together as{" "}
                      <strong>one bill</strong>. Set each amount below (and how
                      often it is collected); the tenant sees a single "Fixed
                      Charges" bill
                      {(() => {
                        const monthly = ["water", "security", "trash"].reduce(
                          (sum, key) =>
                            formik.values[key]?.frequency === "annual"
                              ? sum
                              : sum + (Number(formik.values[key]?.amount) || 0),
                          0
                        );
                        return monthly > 0
                          ? ` of Rs ${monthly.toLocaleString()} in a normal month`
                          : "";
                      })()}
                      . Maintenance, Rent, Electricity and Gas remain separate
                      bills.
                    </Alert>
                  </Grid>
                  <ChargeFrequencyField
                    name="water"
                    label="Water Charges (Rs)"
                    formik={formik}
                  />
                  <ChargeFrequencyField
                    name="security"
                    label="Security Charges (Rs)"
                    formik={formik}
                  />
                  <ChargeFrequencyField
                    name="trash"
                    label="Trash Charges (Rs)"
                    formik={formik}
                  />
                  {/* Bills are always uploaded by the owner — no selection needed. */}

                  {/* Due date, grace period and the late fee itself are one
                      decision from the owner's point of view — "when is rent
                      due, and what happens if it's late" — so they live
                      together here, in this order, instead of the due date
                      being off in a separate Terms step where nothing
                      connects it to the grace period or the fee. */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      sx={{ mb: 0.5 }}
                    >
                      Rent Due Date & Late Payment
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      Pick the day rent is due each month. If a tenant pays
                      later than the grace period below, OTS's platform-wide
                      late fee applies automatically — that part is the same
                      for every property and isn't set here.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Rent Due Date"
                      name="rentDueDate"
                      type="number"
                      InputProps={{ inputProps: { min: 1, max: 28 } }}
                      value={formik.values.rentDueDate}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.rentDueDate &&
                        Boolean(formik.errors.rentDueDate)
                      }
                      helperText={
                        (formik.touched.rentDueDate &&
                          formik.errors.rentDueDate) ||
                        "Day of the month rent is due (1–28)"
                      }
                    />
                  </Grid>

                  <LatePolicyField formik={formik} />
                </Grid>
              </Box>
            </Fade>

            <Fade in={activeStep === 4} timeout={300}>
              <Box sx={{ display: activeStep === 4 ? "block" : "none" }}>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <DescriptionIcon color="primary" /> Agreement Terms
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth>
                      <InputLabel>Lease Duration</InputLabel>
                      <Select
                        name="leaseDuration"
                        value={formik.values.leaseDuration}
                        onChange={formik.handleChange}
                        label="Lease Duration"
                      >
                        {leaseDurationOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth>
                      <InputLabel>Notice Period</InputLabel>
                      <Select
                        name="noticePeriod"
                        value={formik.values.noticePeriod}
                        onChange={formik.handleChange}
                        label="Notice Period"
                      >
                        {noticePeriodOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Yearly Increase %"
                      name="rentIncrease"
                      type="number"
                      InputProps={{ inputProps: { min: 0, max: 50 } }}
                      value={formik.values.rentIncrease}
                      onChange={formik.handleChange}
                    />
                  </Grid>
                </Grid>

                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mt: 3, mb: 1 }}
                >
                  Select conditions for the rental agreement:
                </Typography>
                <Grid container spacing={1}>
                  {landlordConditionOptions.map((condition) => (
                    <Grid item xs={12} sm={6} key={condition.value}>
                      <Tooltip
                        title={condition.description}
                        placement="top"
                        arrow
                      >
                        <Paper
                          elevation={0}
                          onClick={() => {
                            const current =
                              formik.values.landlordConditions || [];
                            if (current.includes(condition.value)) {
                              formik.setFieldValue(
                                "landlordConditions",
                                current.filter((c) => c !== condition.value)
                              );
                            } else {
                              formik.setFieldValue("landlordConditions", [
                                ...current,
                                condition.value,
                              ]);
                            }
                          }}
                          sx={{
                            p: 1.5,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            border: (
                              formik.values.landlordConditions || []
                            ).includes(condition.value)
                              ? `2px solid ${accent}`
                              : "1px solid #e0e0e0",
                            borderRadius: 2,
                            bgcolor: (
                              formik.values.landlordConditions || []
                            ).includes(condition.value)
                              ? accentSoft
                              : "white",
                            transition: "all 0.2s ease",
                            "&:hover": { borderColor: accent },
                          }}
                        >
                          <Checkbox
                            checked={(
                              formik.values.landlordConditions || []
                            ).includes(condition.value)}
                            size="small"
                            sx={{ p: 0 }}
                          />
                          <Typography variant="body2">
                            {condition.label}
                          </Typography>
                        </Paper>
                      </Tooltip>
                    </Grid>
                  ))}
                </Grid>

                <TextField
                  fullWidth
                  label="Custom Conditions (Optional)"
                  name="customConditions"
                  multiline
                  rows={2}
                  placeholder="Add any additional conditions..."
                  value={formik.values.customConditions}
                  onChange={formik.handleChange}
                  sx={{ mt: 2 }}
                />

                <TextField
                  fullWidth
                  label="Note for Tenant (Optional)"
                  name="note"
                  multiline
                  rows={2}
                  placeholder="Any special message for potential tenants..."
                  value={formik.values.note}
                  onChange={formik.handleChange}
                  sx={{ mt: 2 }}
                />

                <Box sx={{ mt: 2, display: "flex", gap: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formik.values.is_active}
                        onChange={(e) =>
                          formik.setFieldValue("is_active", e.target.checked)
                        }
                        color="success"
                      />
                    }
                    // label={formik.values.is_active ? "Active" : "Inactive"}
                    label={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        {formik.values.is_active ? "Active" : "Inactive"}
                        <Tooltip
                          title="If you turn this off, your property will not be shown to other users."
                          arrow
                        >
                          <InfoOutlinedIcon fontSize="small" color="action" />
                        </Tooltip>
                      </Box>
                    }
                  />
                  <FormControl sx={{ minWidth: 150 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mb: 0.5,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Availability
                      </Typography>
                      <Tooltip
                        title="Select 'Available' if the property is vacant and ready for new tenants. Select 'Occupied' if there is currently a tenant living in the property."
                        arrow
                      >
                        <InfoOutlinedIcon fontSize="small" color="action" />
                      </Tooltip>
                    </Box>
                    <Select
                      size="small"
                      name="is_rent"
                      value={formik.values.is_rent}
                      onChange={formik.handleChange}
                    >
                      <MenuItem value={0}>Available</MenuItem>
                      <MenuItem value={1}>Occupied</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Fade>
          </form>
        </Box>

        <Box
          sx={{
            p: 2,
            bgcolor: "#f8f9fa",
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={activeStep === 0}
            startIcon={<NavigateBeforeIcon />}
            sx={{ visibility: activeStep === 0 ? "hidden" : "visible" }}
          >
            Back
          </Button>

          {activeStep === formSteps.length - 1 ? (
            <Button
              variant="contained"
              onClick={formik.handleSubmit}
              disabled={editingFlat && !hasFormChanged(formik.values)}
              endIcon={<CheckCircleIcon />}
              sx={{
                background: `linear-gradient(135deg, ${accent} 0%, ${muiTheme.palette.secondary.main} 100%)`,
                px: 4,
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #0d8274 0%, #2dd66d 100%)",
                },
              }}
            >
              {editingFlat ? "Save Changes" : "Create Property"}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleStepNext}
              endIcon={<NavigateNextIcon />}
              sx={{
                background: `linear-gradient(135deg, ${accent} 0%, ${muiTheme.palette.secondary.main} 100%)`,
              }}
              disabled={formik?.values?.flatName === ""}
            >
              Next
            </Button>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export { LatePolicyField };
export default PropertyFormModal;
